import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, ArrowUpRight, Star, ExternalLink, Navigation, CheckCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

import { trackCall, trackText } from "@/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PLACE = "Dr. Kevin Greenspan, 4573 US 221 N, Marion, NC 28752";
const MAP_EMBED_URL = `https://www.google.com/maps?q=${encodeURIComponent(PLACE)}&z=15&output=embed`;
const DIRECTIONS_URL = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(PLACE)}`;

export default function Footer() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("idle");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all fields.");
      return;
    }
    setLoading(true);
    setStatus("idle");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      toast.success("Message sent! We'll get back to you soon.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      setStatus("error");
      toast.error("Something went wrong. Please call or text us instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer id="contact" className="bg-[#1A221E] py-24 lg:py-32" data-testid="footer-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Top CTA */}
        <div className="mb-20">
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium text-white tracking-tighter leading-none mb-6">
            Ready to Feel <br />Better?
          </h2>
          <p className="font-body text-base text-white/60 max-w-md leading-relaxed">
            Take the first step. Whether it's your first adjustment or you're returning to care, Dr. Greenspan is here for you.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
          {/* Contact info */}
          <div className="space-y-6">
            <h3 className="font-heading text-2xl font-medium text-white tracking-tight">Get in Touch</h3>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                  <MapPin size={14} className="text-[#C87B5D]" />
                </div>
                <div>
                  <p className="font-body text-sm text-white/80">Earth Heart, 4573 US 221 N</p>
                  <p className="font-body text-sm text-white/80">Marion, NC 28752</p>
                  <p className="font-body text-xs text-white/50">Tue &amp; Thu: 10AM–Noon, 3–6PM</p>
                </div>
              </div>
              <div className="flex items-center gap-3" data-testid="footer-black-mountain">
                <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                  <MapPin size={14} className="text-[#C87B5D]" />
                </div>
                <div>
                  <p className="font-body text-sm text-white/80">3174 US Hwy 70 W</p>
                  <p className="font-body text-sm text-white/80">Black Mountain, NC 28711</p>
                  <p className="font-body text-xs text-white/50">Alternate Saturdays: 12:30–3PM · phone bookings only</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                  <Phone size={14} className="text-[#C87B5D]" />
                </div>
                <div className="flex flex-col">
                  <a href="tel:+18286913551" onClick={trackCall("footer")} className="font-body text-sm text-white/80 hover:text-[#C87B5D] transition-colors" data-testid="footer-call-link">Call (828) 691-3551</a>
                  <a href="sms:+18286913551" onClick={trackText("footer")} className="font-body text-sm text-white/80 hover:text-[#C87B5D] transition-colors" data-testid="footer-text-link">Text (828) 691-3551</a>
                  <p className="font-body text-xs text-white/50">Call or text for appointments</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                  <Mail size={14} className="text-[#C87B5D]" />
                </div>
                <p className="font-body text-sm text-white/80">By appointment only</p>
              </div>
            </div>

            {/* Map */}
            <div className="rounded-2xl overflow-hidden border border-white/10 bg-white/5" data-testid="footer-map">
              <iframe
                src={MAP_EMBED_URL}
                title="Map to Earth Heart, 4573 US 221 N, Marion, NC"
                width="100%"
                height="200"
                style={{ border: 0, display: "block", filter: "saturate(0.85)" }}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="footer-map-iframe"
              />
              <a
                href={DIRECTIONS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between gap-3 px-4 py-3 font-body text-sm text-white/80 hover:text-white hover:bg-white/10 transition-colors"
                data-testid="footer-directions-link"
              >
                <span className="flex items-center gap-2">
                  <Navigation size={14} className="text-[#C87B5D]" />
                  Get Directions
                </span>
                <ExternalLink size={12} className="text-[#C87B5D]" />
              </a>
            </div>
          </div>

          {/* Quick links */}
          <div className="space-y-6">
            <h3 className="font-heading text-2xl font-medium text-white tracking-tight">Quick Links</h3>
            <nav className="flex flex-col gap-3">
              {[
                { label: "About Dr. Greenspan", href: "#about" },
                { label: "Services", href: "#services" },
                { label: "Wellness Blog", href: "/blog" },
                { label: "Marion Location", href: "/marion" },
                { label: "Black Mountain Saturdays", href: "/black-mountain" },
                { label: "Book Online", href: "/book-online" },
                { label: "Digital Business Card", href: "/card" },
              ].map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="font-body text-sm text-white/60 hover:text-[#C87B5D] transition-colors flex items-center gap-1 group"
                  data-testid={`footer-link-${link.label.toLowerCase().replace(/ /g, '-')}`}
                >
                  {link.label}
                  <ArrowUpRight size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                </a>
              ))}
            </nav>
            {/* Google Reviews button */}
            <a
              href="https://www.google.com/search?q=kevin+greenspan+chiropractor+reviews"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-white/10 rounded-2xl px-5 py-3 mt-4 hover:bg-white/15 transition-colors"
              data-testid="footer-google-reviews-btn"
            >
              <div className="flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={12} className="text-yellow-500 fill-yellow-500" />
                ))}
              </div>
              <span className="font-body text-sm text-white/80">See our Google reviews</span>
              <ExternalLink size={12} className="text-[#C87B5D]" />
            </a>
          </div>

          {/* Contact form */}
          <div>
            <h3 className="font-heading text-2xl font-medium text-white tracking-tight mb-6">Send a Message</h3>
            {status === "success" ? (
              <div className="rounded-2xl border border-white/10 bg-white/5 p-6" data-testid="contact-success">
                <div className="flex items-center gap-3 mb-3">
                  <CheckCircle size={20} className="text-[#C87B5D]" />
                  <p className="font-heading text-xl text-white">Message sent</p>
                </div>
                <p className="font-body text-sm text-white/70 leading-relaxed mb-4">
                  Thank you — Dr. Greenspan has received your message and will reply shortly.
                </p>
                <button
                  type="button"
                  onClick={() => setStatus("idle")}
                  className="font-body text-sm text-[#C87B5D] hover:underline"
                  data-testid="contact-send-another"
                >
                  Send another message
                </button>
              </div>
            ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
              {status === "error" && (
                <div className="rounded-2xl border border-[#C87B5D]/40 bg-[#C87B5D]/10 p-4 font-body text-sm text-white" data-testid="contact-error">
                  <p className="font-medium mb-1">We couldn't send your message.</p>
                  <p className="text-white/70">
                    Please <a href="tel:+18286913551" className="text-[#C87B5D] font-medium hover:underline">call (828) 691-3551</a> or{" "}
                    <a href="sms:+18286913551" className="text-[#C87B5D] font-medium hover:underline">text us</a> instead.
                  </p>
                </div>
              )}
              <Input
                data-testid="contact-name"
                placeholder="Your name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="rounded-xl border-white/20 bg-white/5 text-white placeholder:text-white/40 h-11 font-body focus-visible:ring-[#C87B5D]"
              />
              <Input
                data-testid="contact-email"
                type="email"
                placeholder="your@email.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="rounded-xl border-white/20 bg-white/5 text-white placeholder:text-white/40 h-11 font-body focus-visible:ring-[#C87B5D]"
              />
              <Textarea
                data-testid="contact-message"
                placeholder="Your message..."
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="rounded-xl border-white/20 bg-white/5 text-white placeholder:text-white/40 min-h-[80px] font-body focus-visible:ring-[#C87B5D]"
              />
              <Button
                data-testid="contact-submit-button"
                type="submit"
                disabled={loading}
                className="w-full rounded-full bg-[#C87B5D] hover:bg-[#b56a4e] text-white font-body h-11"
              >
                {loading ? "Sending..." : "Send Message"}
              </Button>
            </form>
            )}
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-20 pt-8 border-t border-white/10 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="font-heading text-xl text-white/40 tracking-tight">Dr. Greenspan, DC</p>
          <p className="font-body text-xs text-white/30">
            Marion, NC &bull; Applied Kinesiology &bull; Extremity Adjusting &bull; Chiropractic Care
          </p>
        </div>

        <p className="mt-10 text-center font-body text-xs text-white/50" data-testid="footer-copyright">
          © {new Date().getFullYear()} Dr. Kevin Greenspan
        </p>
      </div>
    </footer>
  );
}
